import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Message } from '@shared/types';
import { useConversation } from '@/services/conversationService';
import { useCurrentMessage } from '@/contexts/CurrentMessageContext';
import { useMessagesQuery } from '@/services/messageService';
import Tree from '@shared/Tree';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { ChatSection } from '@/components/chat/ChatSection';
import { Button } from '@/components/ui/button';
import {
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  MessageSquare,
  Box,
  SlidersHorizontal,
} from 'lucide-react';
import { ViewerSection } from '@/components/viewer/ViewerSection';
import { ParameterSection } from '@/components/parameter/ParameterSection';
import { useBlob } from '@/contexts/BlobContext';
import { useColor } from '@/contexts/ColorContext';
import { useMeshFiles } from '@/contexts/MeshFilesContext';
import { getConversationMeshPath } from '@/lib/firebaseAssetPaths';
import { downloadConversationAssetBlob } from '@/lib/conversationAssets';
import { toPlainMessage } from '@/utils/plainMessage';

const PANEL_SIZES = {
  CHAT: {
    DEFAULT: 25,
    MIN: 384,
    MAX: 550,
  },
  PREVIEW: {
    MIN: 20,
  },
  PARAMETERS: {
    DEFAULT: 30,
    MIN: 320,
    MAX: 384,
  },
} as const;

type MobileTab = 'chat' | 'preview' | 'params';

export function ParametricEditor() {
  const { t } = useTranslation('common');
  const { conversation } = useConversation();
  const { currentMessage, setCurrentMessage } = useCurrentMessage();
  const { setBlob } = useBlob();
  const { setColor } = useColor();
  const { hasMeshFile, setMeshFile } = useMeshFiles();
  const [isParametersPanelCollapsed, setIsParametersPanelCollapsed] =
    useState(false);
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );
  const [mobileTab, setMobileTab] = useState<MobileTab>('preview');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const { data: messages = [] } = useMessagesQuery();

  const lastMessage = useMemo(() => {
    if (conversation.current_message_leaf_id) {
      return messages.find(
        (msg) => msg.id === conversation.current_message_leaf_id,
      );
    }
    return messages[messages.length - 1];
  }, [messages, conversation.current_message_leaf_id]);

  const messageTree = useMemo(() => {
    return new Tree<Message>(messages);
  }, [messages]);

  const currentMessageBranch = useMemo(() => {
    return messageTree.getPath(lastMessage?.id ?? '');
  }, [lastMessage, messageTree]);

  useEffect(() => {
    setCurrentMessage(null);
    setBlob(null);
    setColor('#56B6C6');
  }, [conversation.id, setCurrentMessage, setBlob, setColor]);

  useEffect(() => {
    if (conversation.mode === 'mesh' || conversation.pendingMeshId) return;
    if (lastMessage?.role === 'assistant') {
      setCurrentMessage(toPlainMessage(lastMessage));
    }
  }, [
    lastMessage,
    setCurrentMessage,
    conversation.mode,
    conversation.pendingMeshId,
  ]);

  useEffect(() => {
    let cancelled = false;

    const restoreMeshFiles = async () => {
      const meshMessages = currentMessageBranch.filter(
        (message) =>
          message.role === 'user' &&
          message.content.mesh &&
          message.content.meshFilename,
      );

      await Promise.all(
        meshMessages.map(async (message) => {
          const mesh = message.content.mesh;
          const filename = message.content.meshFilename;

          if (!mesh || !filename || hasMeshFile(filename)) {
            return;
          }

          try {
            const meshBlob = await downloadConversationAssetBlob(
              conversation.id,
              getConversationMeshPath(
                conversation.user_id,
                conversation.id,
                mesh.id,
                mesh.fileType,
              ),
            );

            if (!cancelled) {
              setMeshFile(filename, meshBlob);
            }
          } catch (error) {
            console.error('[MeshFiles] Failed to restore mesh from storage:', error);
          }
        }),
      );
    };

    if (conversation.id && conversation.user_id && currentMessageBranch.length > 0) {
      restoreMeshFiles();
    }

    return () => {
      cancelled = true;
    };
  }, [
    conversation.id,
    conversation.user_id,
    currentMessageBranch,
    hasMeshFile,
    setMeshFile,
  ]);

  const setContainerRef = useCallback((element: HTMLDivElement) => {
    setContainerWidth(element.offsetWidth);
    resizeObserverRef.current = new ResizeObserver(() => {
      setContainerWidth(element.offsetWidth);
    });
    resizeObserverRef.current.observe(element);
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, []);

  const chatPanelSizes = useMemo(() => {
    if (containerWidth === 0)
      return { defaultSize: 30, minSize: 0, maxSize: 100 };
    const minSize = (PANEL_SIZES.CHAT.MIN / containerWidth) * 100;
    const maxSize = (PANEL_SIZES.CHAT.MAX / containerWidth) * 100;
    const defaultSize = Math.min(
      Math.max(PANEL_SIZES.CHAT.DEFAULT, minSize),
      maxSize,
    );
    return { defaultSize, minSize, maxSize };
  }, [containerWidth]);

  const parametersPanelSizes = useMemo(() => {
    if (containerWidth === 0)
      return { defaultSize: 25, minSize: 15, maxSize: 30 };
    const chatMinPixels = PANEL_SIZES.CHAT.MIN;
    const previewMinPixels = (PANEL_SIZES.PREVIEW.MIN / 100) * containerWidth;
    const availableForParameters =
      containerWidth - chatMinPixels - previewMinPixels;
    const maxPixelsAvailable = Math.min(
      PANEL_SIZES.PARAMETERS.MAX,
      availableForParameters,
    );
    const requestedMinSize = (PANEL_SIZES.PARAMETERS.MIN / containerWidth) * 100;
    const maxSize = Math.max(0, (maxPixelsAvailable / containerWidth) * 100);
    // On narrower desktop widths, chat min + preview min can leave less than
    // the preferred 320px for parameters. Clamp the parameter panel to the
    // available space so react-resizable-panels never receives impossible
    // constraints where default/min sizes exceed 100% combined.
    const adjustedMinSize = Math.min(requestedMinSize, maxSize);
    const defaultSize = Math.min(
      Math.max(PANEL_SIZES.PARAMETERS.DEFAULT, adjustedMinSize),
      maxSize,
    );
    return {
      defaultSize,
      minSize: adjustedMinSize,
      maxSize,
    };
  }, [containerWidth]);

  const hasArtifact = useMemo(
    () => !!currentMessage?.content.artifact,
    [currentMessage],
  );

  const handleChatCollapse = useCallback(() => {
    setIsChatCollapsed(true);
  }, []);
  const handleChatExpand = useCallback(() => {
    setIsChatCollapsed(false);
  }, []);
  const handleParametersCollapse = useCallback(() => {
    setIsParametersPanelCollapsed(true);
  }, []);
  const handleParametersExpand = useCallback(() => {
    setIsParametersPanelCollapsed(false);
  }, []);

  // ───────────────────────── Mobile (tabbed) ─────────────────────────
  if (isMobile) {
    const tabs: { id: MobileTab; icon: React.ReactNode; label: string }[] = [
      { id: 'chat', icon: <MessageSquare className="h-5 w-5" />, label: t('editor.chatVertical') || 'Chat' },
      { id: 'preview', icon: <Box className="h-5 w-5" />, label: 'Preview' },
      ...(hasArtifact
        ? [{ id: 'params' as MobileTab, icon: <SlidersHorizontal className="h-5 w-5" />, label: 'Params' }]
        : []),
    ];

    return (
      <div
        className="flex h-full w-full flex-col overflow-hidden bg-k-canvas"
        ref={setContainerRef}
      >
        <div className="relative min-h-0 flex-1 overflow-hidden">
          <div className={`absolute inset-0 ${mobileTab === 'chat' ? 'block' : 'hidden'}`}>
            <ChatSection messages={currentMessageBranch ?? []} />
          </div>
          <div className={`absolute inset-0 ${mobileTab === 'preview' ? 'block' : 'hidden'}`}>
            <ViewerSection />
          </div>
          {hasArtifact && (
            <div className={`absolute inset-0 ${mobileTab === 'params' ? 'block' : 'hidden'}`}>
              <ParameterSection />
            </div>
          )}
        </div>

        {/* Bottom tab bar */}
        <nav
          className="flex flex-shrink-0 border-t-2 border-brand-indigo bg-k-card mb-safe"
          role="tablist"
          aria-label="Editor sections"
        >
          {tabs.map((tab) => {
            const active = mobileTab === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={active}
                onClick={() => setMobileTab(tab.id)}
                className={`relative flex flex-1 flex-col items-center justify-center gap-1 py-3 text-xs font-bold transition-colors ${
                  active
                    ? 'bg-brand-teal text-brand-indigo'
                    : 'text-brand-indigo/60 active:bg-brand-indigo/5'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    );
  }

  // ───────────────────────── Desktop (resizable) ─────────────────────────
  return (
    <div
      className="flex h-full w-full overflow-hidden bg-k-canvas"
      ref={setContainerRef}
    >
      {/* Collapsed chat strip */}
      {isChatCollapsed && (
        <div className="flex h-full w-10 flex-shrink-0 flex-col items-center border-r border-k-line bg-k-card">
          <button
            aria-label={t('editor.expandChat')}
            onClick={handleChatExpand}
            className="mt-3 flex h-7 w-7 items-center justify-center rounded-md text-brand-indigo transition-colors hover:bg-brand-indigo/10"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </button>
          <div className="flex flex-1 items-center justify-center">
            <span className="rotate-180 transform text-[10px] font-bold uppercase tracking-[0.16em] text-brand-indigo/60 [writing-mode:vertical-rl]">
              {t('editor.chatVertical')}
            </span>
          </div>
        </div>
      )}

      <PanelGroup
        key={`${hasArtifact ? 'p' : 'np'}-${isChatCollapsed ? 'cc' : 'co'}-${isParametersPanelCollapsed ? 'pc' : 'po'}`}
        direction="horizontal"
        className="h-full w-full"
      >
        {!isChatCollapsed && (
          <>
            <Panel
              defaultSize={chatPanelSizes.defaultSize}
              minSize={chatPanelSizes.minSize}
              maxSize={chatPanelSizes.maxSize}
              id="chat-panel"
              order={0}
            >
              <div className="relative h-full">
                <ChatSection messages={currentMessageBranch ?? []} />
                <Button
                  variant="ghost"
                  aria-label={t('editor.collapseChat')}
                  onClick={handleChatCollapse}
                  className="absolute right-2 top-1.5 z-10 h-8 w-8 rounded-md p-0 text-brand-indigo/70 transition-colors hover:bg-brand-indigo/10 hover:text-brand-indigo"
                >
                  <PanelLeftClose className="h-4 w-4" />
                </Button>
              </div>
            </Panel>
            <PanelResizeHandle className="resize-handle group relative" />
          </>
        )}

        <Panel
          defaultSize={
            100 -
            (isChatCollapsed ? 0 : chatPanelSizes.defaultSize) -
            (hasArtifact && !isParametersPanelCollapsed ? parametersPanelSizes.defaultSize : 0)
          }
          minSize={PANEL_SIZES.PREVIEW.MIN}
          id="preview-panel"
          order={1}
        >
          <div className="relative h-full w-full overflow-hidden">
            <ViewerSection />
          </div>
        </Panel>

        {hasArtifact && !isParametersPanelCollapsed && (
          <>
            <PanelResizeHandle className="resize-handle group relative" />
            <Panel
              defaultSize={parametersPanelSizes.defaultSize}
              minSize={parametersPanelSizes.minSize}
              maxSize={parametersPanelSizes.maxSize}
              id="parameters-panel"
              order={2}
            >
              <div className="relative h-full">
                <ParameterSection />
                <Button
                  variant="ghost"
                  aria-label={t('editor.collapseOutput')}
                  onClick={handleParametersCollapse}
                  className="absolute left-2 top-1.5 z-10 h-8 w-8 rounded-md p-0 text-brand-indigo/70 transition-colors hover:bg-brand-indigo/10 hover:text-brand-indigo"
                >
                  <PanelRightClose className="h-4 w-4" />
                </Button>
              </div>
            </Panel>
          </>
        )}
      </PanelGroup>

      {hasArtifact && isParametersPanelCollapsed && (
        <div className="flex h-full w-10 flex-shrink-0 flex-col items-center border-l border-k-line bg-k-card">
          <Button
            variant="ghost"
            aria-label={t('editor.expandOutput')}
            onClick={handleParametersExpand}
            className="mt-2 h-8 w-8 rounded-md p-0 text-brand-indigo/70 transition-colors hover:bg-brand-indigo/10 hover:text-brand-indigo"
          >
            <PanelRightOpen className="h-4 w-4" />
          </Button>
          <div className="flex flex-1 items-center justify-center">
            <span className="rotate-180 transform text-xs font-bold tracking-wider text-brand-indigo/60 [writing-mode:vertical-rl]">
              {t('editor.outputVertical')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
